import { getClientRuntimeConfig } from '@/config/runtime/clientRuntimeConfig';

const SW_URL = '/sw.js';

/** Longest a download waits for offline mode to be set up before reporting success anyway. */
const OFFLINE_READY_TIMEOUT_MS = 30 * 1000;

export function supportsServiceWorker(): boolean {
    return typeof window !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Registers the offline service worker. Safe to call repeatedly: the browser returns the existing
 * registration (and only reinstalls if sw.js changed).
 * `type: 'module'` because sw.js is an ES-module bundle; `updateViaCache: 'none'` so update checks skip the HTTP cache.
 */
export function registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.register(SW_URL, { type: 'module', updateViaCache: 'none' });
}

/**
 * Registers the service worker for this page load, once the page has loaded, the browser is idle and we're online,
 * so the ~4 MB offline shell precache doesn't compete with the page's own chunks. If the install fails, it's tried
 * again on the next page load or download.
 */
export function startServiceWorkerRegistration(): () => void {
    let cancelled = false;
    const cleanups: Array<() => void> = [];

    const register = () => {
        if (cancelled) return;
        registerServiceWorker()
            .then((registration) => {
                if (!cancelled) cleanups.push(promoteUpdates(registration));
            })
            .catch(logRegistrationError);
    };

    const scheduleRegistration = () => {
        if (navigator.onLine) cleanups.push(runWhenIdle(register));
    };
    if (document.readyState === 'complete') {
        scheduleRegistration();
    } else {
        window.addEventListener('load', scheduleRegistration, { once: true });
        cleanups.push(() => window.removeEventListener('load', scheduleRegistration));
    }

    return () => {
        cancelled = true;
        cleanups.forEach((cleanup) => cleanup());
    };
}

/** Runs `callback` when the browser is idle (Safari has no requestIdleCallback). */
export function runWhenIdle(callback: () => void): () => void {
    if ('requestIdleCallback' in window) {
        const handle = window.requestIdleCallback(callback, { timeout: 5000 });
        return () => window.cancelIdleCallback(handle);
    }
    const handle = setTimeout(callback, 1000);
    return () => clearTimeout(handle);
}

/**
 * Nudges an updated worker to activate instead of getting stuck "waiting" behind the old one
 * (Serwist calls skipWaiting() at eval time, which doesn't reliably promote it).
 */
function promoteUpdates(registration: ServiceWorkerRegistration): () => void {
    const cleanups: Array<() => void> = [];

    if (registration.waiting) promote(registration.waiting);

    const handleUpdateFound = () => {
        const installing = registration.installing;
        if (!installing) return;

        const handleStateChange = () => {
            if (installing.state === 'installed') promote(installing);
        };
        installing.addEventListener('statechange', handleStateChange);
        cleanups.push(() => installing.removeEventListener('statechange', handleStateChange));
    };
    registration.addEventListener('updatefound', handleUpdateFound);
    cleanups.push(() => registration.removeEventListener('updatefound', handleUpdateFound));

    return () => cleanups.forEach((cleanup) => cleanup());
}

const promote = (worker: ServiceWorker | null) => worker?.postMessage({ type: 'SKIP_WAITING' });

const logRegistrationError = (err: unknown) => {
    if (isMissingLocalServiceWorker(err)) return;
    console.error('[SW] Registration failed:', err);
};

/** True when registering failed because sw.js doesn't exist, which is expected in local watch mode (it isn't built). */
const isMissingLocalServiceWorker = (err: unknown): boolean => {
    if (getClientRuntimeConfig()?.environment !== 'local') return false;
    const name = String((err as { name?: string } | undefined)?.name ?? '');
    const message = String((err as { message?: string } | undefined)?.message ?? err ?? '').toLowerCase();

    const referencesSwScript =
        message.includes('/sw.js') ||
        message.includes('serviceworker script') ||
        message.includes('failed to register a serviceworker');

    return referencesSwScript && name === 'TypeError';
};

/**
 * Resolves once a service worker is active, i.e. the offline shell is fully precached and offline navigation works.
 * Rejects if the install fails (e.g. the device went offline mid-precache) or doesn't finish within the timeout.
 * The next page load or download registers again, and only fetches what's still missing.
 */
export async function ensureOfflineReady(timeoutMs = OFFLINE_READY_TIMEOUT_MS): Promise<void> {
    if (!supportsServiceWorker()) throw new Error('Service workers are not supported in this browser.');

    const registration = await registerServiceWorker();
    if (registration.active) return;

    const result = await waitForActivation(registration, timeoutMs);
    if (result === 'failed') throw new Error('Service worker installation failed.');
    if (result === 'timeout') throw new Error('Offline mode was not set up before the timeout.');
}

function waitForActivation(
    registration: ServiceWorkerRegistration,
    timeoutMs: number
): Promise<'activated' | 'failed' | 'timeout'> {
    return new Promise((resolve) => {
        let worker: ServiceWorker | null = null;
        const timer = setTimeout(() => finish('timeout'), timeoutMs);

        function finish(result: 'activated' | 'failed' | 'timeout') {
            clearTimeout(timer);
            registration.removeEventListener('updatefound', onUpdateFound);
            worker?.removeEventListener('statechange', onStateChange);
            resolve(result);
        }

        function onStateChange() {
            if (!worker) return;
            if (worker.state === 'activated') finish('activated');
            // A newer worker (e.g. installed from another tab) can replace this one.
            else if (worker.state === 'redundant') finish(registration.active ? 'activated' : 'failed');
        }

        function onUpdateFound() {
            watch(registration.installing);
        }

        function watch(next: ServiceWorker | null) {
            if (!next) return;
            worker?.removeEventListener('statechange', onStateChange);
            worker = next;
            worker.addEventListener('statechange', onStateChange);
            onStateChange();
        }

        // `waiting` covers a worker that finished installing but hasn't been activated yet.
        const pending = registration.installing ?? registration.waiting;
        if (!pending) {
            finish(registration.active ? 'activated' : 'failed');
            return;
        }
        registration.addEventListener('updatefound', onUpdateFound);
        watch(pending);
    });
}
